var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { extractErrorMessage, isCommerceOffer, isCompleteCommerceItem, OfferSelectionContainer, } from '@themaven-net/commerce-shared';
import { LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
/**
 * Base class for components that interact directly with single items that have multiple offers
 */
export default class BaseOfferAwareComponent extends LitElement {
    constructor() {
        super(...arguments);
        /** Should this element adopt any pre-existing child elements */
        this.adoptChildren = false;
        /** Array of child elements that were existing prior to connected callback */
        this.preExistingChildren = [];
    }
    /**
     * The primary offer that has been selected for this element
     */
    get primaryOffer() {
        return this.offerSelection?.primaryOffer;
    }
    /**
     * All offers that have been selected for this element
     */
    get selectedOffers() {
        return this.offerSelection?.selectedOffers ?? [];
    }
    get itemTitle() {
        return this.item?.title;
    }
    /**
     * Template method that returns the preferred merchants for the environment
     *
     * Override this to implement per-environment logic
     */
    get preferredMerchants() {
        return [];
    }
    /**
     * Template method that returns the preferred affiliates for the environment
     *
     * Override this to implement per-environment logic
     */
    get preferredAffiliates() {
        return [];
    }
    /**
     * Template method that returns the number of offers to show
     *
     * Override this to implement per-environment logic
     */
    get offerCount() {
        return 1;
    }
    /**
     * Ease of access getter
     */
    get item() {
        if (this.config?.item === undefined || !isCompleteCommerceItem(this.config.item)) {
            return undefined;
        }
        return this.config.item;
    }
    /**
     * Cache pre-existing child nodes if 'adoptChildren' is true, this is to simulate slot behavior
     * in open shadow roots.
     */
    connectedCallback() {
        super.connectedCallback();
        if (this.adoptChildren === true) {
            this.preExistingChildren = Array.from(this.childNodes);
        }
    }
    /**
     * make the call to append the previously identified children onto the
     * marked append target
     */
    updated(changedProperties) {
        super.updated(changedProperties);
        if (this.adoptChildren === true) {
            this.appendFilteredChildren(this.appendTarget);
        }
    }
    /**
     * Append the previously selected and filtered child nodes to marked append target
     */
    appendFilteredChildren(appendTarget) {
        if (appendTarget === null || appendTarget === undefined) {
            console.warn('unable to append, target is null or undefined', appendTarget);
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.preExistingChildren
            .map((child) => this.filterAppendList(child, appendTarget))
            .flat()
            .forEach((child) => {
            try {
                this.removeChild(child);
            }
            catch {
                /* no op */
            }
            try {
                appendTarget.appendChild(child);
            }
            catch (e) {
                /* no op */
                console.warn('Unable to append child element', extractErrorMessage(e), child);
            }
        });
    }
    /**
     * Identify any nodes that should be appended on render
     */
    filterAppendList(child, appendTarget) {
        if (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) {
            // prevent empty nodes because reasons
            return [];
        }
        if (appendTarget.isSameNode(child)) {
            return [];
        }
        return [child];
    }
    /**
     * Ease of access getter
     */
    get offers() {
        return this.item?.offers.filter(isCommerceOffer) ?? [];
    }
    /**
     * Ease of access getter
     */
    get images() {
        return this.item?.images ?? [];
    }
    selectOffers(item) {
        this.offerSelection = new OfferSelectionContainer(item?.offers ?? [], this.preferredMerchants, this.preferredAffiliates, this.offerCount);
    }
    /**
     * Leaveing shadow dom open for 3rd party scripts to find links
     */
    createRenderRoot() {
        return this;
    }
}
__decorate([
    property({ type: Object }),
    __metadata("design:type", Object)
], BaseOfferAwareComponent.prototype, "config", void 0);
__decorate([
    query('.appendTarget'),
    __metadata("design:type", Object)
], BaseOfferAwareComponent.prototype, "appendTarget", void 0);
//# sourceMappingURL=baseOfferAwareComponent.js.map